/**
 * GapToolbar - Top toolbar for gap/silence removal mode.
 * Rule-based design: "Gaps longer than X → shorten to Y"
 * Compact layout matching FillerWordToolbar.
 * Follows Libretto's calm, editorial design system.
 */
import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Box,
    IconButton,
    Button,
    Typography,
    TextField,
    InputAdornment,
    CircularProgress,
} from '@mui/material';
import {
    Close as CloseIcon,
    KeyboardArrowUp as ArrowUpIcon,
    KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material';
import { GapStore } from './GapStore';
import { CompositionService } from '../../../librettoEditor/services/CompositionService';
import { ScriptLayerStore } from '../../../librettoEditor/store/ScriptLayerStore';

interface GapToolbarProps {
    onShortenCurrent: () => void;
    onShortenAll: () => void;
    onClose: () => void;
}

const GapToolbar: React.FC<GapToolbarProps> = observer(({
    onShortenCurrent,
    onShortenAll,
    onClose,
}) => {
    // Local state for text field values (allows typing before committing)
    const [minGapText, setMinGapText] = useState(String(GapStore.minGapThreshold));
    const [shortenToText, setShortenToText] = useState(String(GapStore.shortenToSeconds));

    // Sync local state when store changes externally
    useEffect(() => {
        setMinGapText(String(GapStore.minGapThreshold));
    }, [GapStore.minGapThreshold]);

    useEffect(() => {
        setShortenToText(String(GapStore.shortenToSeconds));
    }, [GapStore.shortenToSeconds]);

    const handleNavigate = (direction: 'prev' | 'next') => {
        if (direction === 'prev') {
            GapStore.goToPrevious();
        } else {
            GapStore.goToNext();
        }

        // Seek to the current gap - map source time through segments
        const current = GapStore.currentGap;
        if (current) {
            const compTime = ScriptLayerStore.mapSourceTimeToCompositionTime(current.startSec);
            if (compTime !== null) {
                CompositionService.seek(compTime);
            } else {
                CompositionService.seek(current.startSec);
            }
        }
    };

    // Commit min gap threshold on blur or Enter
    const commitMinGap = () => {
        const value = parseFloat(minGapText);
        if (!isNaN(value) && value >= 0) {
            void GapStore.setMinGapThreshold(value);
        } else {
            setMinGapText(String(GapStore.minGapThreshold));
        }
    };

    // Commit shorten-to value on blur or Enter
    const commitShortenTo = () => {
        const value = parseFloat(shortenToText);
        if (!isNaN(value) && value >= 0) {
            GapStore.setShortenToSeconds(value);
        } else {
            setShortenToText(String(GapStore.shortenToSeconds));
        }
    };

    const handleMinGapKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            commitMinGap();
            (e.target as HTMLInputElement).blur();
        }
    };

    const handleShortenToKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            commitShortenTo();
            (e.target as HTMLInputElement).blur();
        }
    };

    const isLoading = GapStore.isRemoving;
    const totalRemovedSec = GapStore.totalSecondsRemoved;
    const shortenableCount = GapStore.shortenableCount;

    const formatSavings = (seconds: number): string => {
        if (!Number.isFinite(seconds) || seconds <= 0) return '0s';
        if (seconds < 1) return `${Math.round(seconds * 1000)}ms`;
        if (seconds < 60) return `${seconds.toFixed(1)}s`;
        let mins = Math.floor(seconds / 60);
        let secs = Math.round(seconds % 60);
        if (secs === 60) {
            mins += 1;
            secs = 0;
        }
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <Box
            sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                backgroundColor: 'var(--app-bg-surface)',
                padding: '14px 16px',
                paddingRight: '24px',
                gap: 1.5,
                minHeight: 56,
            }}
        >
            {/* Close button - top right */}
            <IconButton
                size="small"
                onClick={onClose}
                sx={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    padding: '4px',
                    color: 'var(--app-text-subtle)',
                    '&:hover': { backgroundColor: 'var(--app-bg-subtle)', color: 'var(--app-text-primary)' },
                }}
            >
                <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>

            {/* Rule: "Gaps longer than X → shorten to Y" + summary */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography sx={{ fontSize: 13, color: 'var(--app-text-muted)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        Gaps longer than
                    </Typography>
                    <TextField
                        size="small"
                        type="text"
                        value={minGapText}
                        onChange={(e) => setMinGapText(e.target.value)}
                        onBlur={commitMinGap}
                        onKeyDown={handleMinGapKeyDown}
                        inputProps={{
                            style: {
                                textAlign: 'center',
                                padding: '4px 6px',
                                width: '28px',
                                fontWeight: 500,
                                fontSize: 13,
                                color: 'var(--app-text-primary)',
                            },
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Typography sx={{ fontSize: 12, color: 'var(--app-text-subtle)' }}>s</Typography>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            flexShrink: 0,
                            '& .MuiOutlinedInput-root': {
                                height: '28px',
                                borderRadius: '6px',
                                color: 'var(--app-text-primary)',
                                '& fieldset': { borderColor: 'var(--app-border-subtle)' },
                                '&:hover fieldset': { borderColor: 'var(--app-border-muted)' },
                                '&.Mui-focused fieldset': { borderColor: '#2B6BFD', borderWidth: 1 },
                            },
                        }}
                    />

                    <Typography sx={{ fontSize: 13, color: 'var(--app-text-muted)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        shorten to
                    </Typography>
                    <TextField
                        size="small"
                        type="text"
                        value={shortenToText}
                        onChange={(e) => setShortenToText(e.target.value)}
                        onBlur={commitShortenTo}
                        onKeyDown={handleShortenToKeyDown}
                        inputProps={{
                            style: {
                                textAlign: 'center',
                                padding: '4px 6px',
                                width: '28px',
                                fontWeight: 500,
                                fontSize: 13,
                                color: 'var(--app-text-primary)',
                            },
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Typography sx={{ fontSize: 12, color: 'var(--app-text-subtle)' }}>s</Typography>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            flexShrink: 0,
                            '& .MuiOutlinedInput-root': {
                                height: '28px',
                                borderRadius: '6px',
                                color: 'var(--app-text-primary)',
                                '& fieldset': { borderColor: 'var(--app-border-subtle)' },
                                '&:hover fieldset': { borderColor: 'var(--app-border-muted)' },
                                '&.Mui-focused fieldset': { borderColor: '#2B6BFD', borderWidth: 1 },
                            },
                        }}
                    />
                </Box>

                <Typography sx={{ fontSize: 12, color: 'var(--app-text-subtle)', lineHeight: 1.2 }}>
                    Will remove {formatSavings(totalRemovedSec)}{shortenableCount > 0 ? ` across ${shortenableCount} gap${shortenableCount === 1 ? '' : 's'}` : ''}
                </Typography>
            </Box>

            {/* Counter and navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                {isLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 50, px: 0.5 }}>
                        <CircularProgress size={12} sx={{ color: 'var(--app-text-subtle)' }} />
                        <Typography sx={{ fontSize: 12, color: 'var(--app-text-subtle)' }}>
                            {GapStore.removalProgress}%
                        </Typography>
                    </Box>
                ) : (
                    <Typography sx={{ fontSize: 13, color: 'var(--app-text-primary)', fontWeight: 600, minWidth: 40, textAlign: 'center', px: 0.5 }}>
                        {GapStore.currentDisplayIndex} / {GapStore.totalCount}
                    </Typography>
                )}

                <IconButton
                    size="small"
                    onClick={() => handleNavigate('prev')}
                    disabled={GapStore.totalCount === 0 || isLoading}
                    sx={{
                        padding: '3px',
                        color: 'var(--app-text-muted)',
                        border: '1px solid var(--app-border-subtle)',
                        borderRadius: '5px',
                        '&:hover': { backgroundColor: 'var(--app-bg-subtle)', color: 'var(--app-text-primary)', borderColor: 'var(--app-border-muted)' },
                        '&.Mui-disabled': { color: 'var(--app-border-muted)', borderColor: 'var(--app-bg-subtle)' },
                    }}
                >
                    <ArrowUpIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                    size="small"
                    onClick={() => handleNavigate('next')}
                    disabled={GapStore.totalCount === 0 || isLoading}
                    sx={{
                        padding: '3px',
                        color: 'var(--app-text-muted)',
                        border: '1px solid var(--app-border-subtle)',
                        borderRadius: '5px',
                        '&:hover': { backgroundColor: 'var(--app-bg-subtle)', color: 'var(--app-text-primary)', borderColor: 'var(--app-border-muted)' },
                        '&.Mui-disabled': { color: 'var(--app-border-muted)', borderColor: 'var(--app-bg-subtle)' },
                    }}
                >
                    <ArrowDownIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Box>

            {/* Action buttons - stacked vertically */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flexShrink: 0 }}>
                <Button
                    variant="text"
                    size="small"
                    onClick={onShortenCurrent}
                    disabled={GapStore.totalCount === 0 || isLoading}
                    sx={{
                        color: 'var(--app-text-primary)',
                        textTransform: 'none',
                        fontSize: 13,
                        fontWeight: 500,
                        padding: '4px 12px',
                        borderRadius: '6px',
                        border: '1px solid var(--app-border-subtle)',
                        minWidth: 'auto',
                        lineHeight: 1.4,
                        '&:hover': {
                            backgroundColor: 'var(--app-bg-subtle)',
                            borderColor: 'var(--app-border-muted)',
                        },
                        '&.Mui-disabled': { color: 'var(--app-border-muted)', borderColor: 'var(--app-bg-subtle)' },
                    }}
                >
                    Shorten
                </Button>
                <Button
                    data-tour="gap-shorten-all"
                    variant="text"
                    size="small"
                    onClick={onShortenAll}
                    disabled={GapStore.totalCount === 0 || isLoading}
                    sx={{
                        color: 'var(--app-bg-surface)',
                        backgroundColor: 'var(--app-text-primary)',
                        textTransform: 'none',
                        fontSize: 13,
                        fontWeight: 500,
                        padding: '4px 12px',
                        borderRadius: '6px',
                        minWidth: 'auto',
                        lineHeight: 1.4,
                        '&:hover': {
                            backgroundColor: 'var(--app-text-primary)',
                            opacity: 0.85,
                        },
                        '&.Mui-disabled': { color: 'var(--app-text-subtle)', backgroundColor: 'var(--app-bg-subtle)' },
                    }}
                >
                    Shorten all ({GapStore.totalCount})
                </Button>
            </Box>
        </Box>
    );
});

export default GapToolbar;
